import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { FormattedMessage } from 'dibs-react-intl';

import { PdpSharedDescriptionAbout } from '../PdpSharedDescriptions/PdpSharedDescriptionAbout';
import { PdpSharedDescriptionAdditionalOptions } from '../PdpSharedDescriptions/PdpSharedDescriptionAdditionalOptions';
import { PdpRespDetails } from '../PdpRespDetails/PdpRespDetails';

import { getAttributeInfo } from '../helpers/getAttributeInfo';

import { PdpRespAboutItemPanel_item$key } from './__generated__/PdpRespAboutItemPanel_item.graphql';
import { PdpRespAboutItemPanel_user$key } from './__generated__/PdpRespAboutItemPanel_user.graphql';

import styles from './PdpRespAboutItemPanel.scss';

type Props = {
    showHeader?: boolean;
    item: PdpRespAboutItemPanel_item$key;
    user: PdpRespAboutItemPanel_user$key;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    dataTn?: string;
};

export const aboutHeader = (
    <FormattedMessage id="PdpRespAboutItemPanel.aboutHeader" defaultMessage="About the Item" />
);

const itemFragment = graphql`
    fragment PdpRespAboutItemPanel_item on Item {
        isCustomizable
        isDyoCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
        ...PdpSharedDescriptionAbout_item
        ...PdpSharedDescriptionAdditionalOptions_item
        ...PdpRespDetails_item
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutItemPanel_user on User {
        ...PdpSharedDescriptionAdditionalOptions_user
        ...PdpRespDetails_user
    }
`;

export const PdpRespAboutItemPanel: FC<Props> = ({
    showHeader,
    item: itemRef,
    user: userRef,
    currency,
    selectedSkuId,
    showExpandingArea,
    dataTn,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const isClient = useClientState();

    const { isCustomizable = null, isDyoCustomizable, attributes } = item || {};
    const { customizationOptions = null, isResizable = false } = getAttributeInfo(attributes);

    const showCustomizationOptions =
        (isCustomizable && customizationOptions) || isDyoCustomizable || isResizable;

    // For collapsed height, set const to = [# of lines to show] * [text line-height]
    const ABOUT_NUM_LINES = (showCustomizationOptions ? 3 : 5) * 30;

    return (
        <GridRow dataTn={dataTn}>
            <GridCol desktop={5} tabletPortrait={12}>
                <div className={styles.descriptionWrapper}>
                    <PdpSharedDescriptionAbout
                        item={item}
                        headerClass={showHeader ? styles.headerText : styles.isHidden}
                        contentClass={styles.paragraphText}
                        title={aboutHeader}
                        collapsedHeight={ABOUT_NUM_LINES}
                        showExpandingArea={showExpandingArea}
                    />
                </div>

                {/* Additional Options */}
                <PdpSharedDescriptionAdditionalOptions
                    buttonType="link"
                    user={user}
                    item={item}
                    headerClass={styles.headerText}
                    contentClass={styles.paragraphText}
                    ctaClass={styles.ctaWrapper}
                    showDyoVersion
                />
            </GridCol>
            <GridCol
                desktop={6}
                desktopOptions={{
                    offset: 1,
                }}
                tabletPortrait={12}
            >
                {/* Details */}
                <PdpRespDetails
                    variant="pdpRedesignVariant"
                    showHeader={false}
                    isClient={isClient}
                    currency={currency}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    user={user}
                />
            </GridCol>
        </GridRow>
    );
};
